import { useQuery } from '@tanstack/react-query';
import { getAllSlackChannels } from "@/helpers/apis";

/**
 * Custom hook to fetch Slack channels and provide utility methods for channel data
 * @param enabled - Whether to enable the query (default: true)
 * @returns Object containing Slack channels data, loading state, and utility methods
 */
export const useSlackChannels = (enabled = true) => {
  // Fetch Slack channels
  const {
    data: slackChannels,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["slack-channels"],
    queryFn: getAllSlackChannels,
    enabled,
  });

  // Method to get channel names by IDs
  const getChannelNamesByIds = (ids: number[]): string[] => {
    if (!slackChannels) return [];

    return ids
      .map(id => {
        const channel = slackChannels.find(ch => ch.id === String(id));
        return channel ? `#${channel.config.name}` : null;
      })
      .filter(Boolean) as string[];
  };

  // Method to get a single channel name by ID
  const getChannelNameById = (id: number): string | null => {
    if (!slackChannels) return null;

    const channel = slackChannels.find(ch => ch.id === String(id));
    return channel ? `#${channel.config.name}` : null;
  };

  // Method to format channel names for display
  const formatChannelDisplay = (ids: number[]): string => {
    // Handle undefined or empty ids
    if (!ids || ids.length === 0) {
      return "0 channels";
    }

    const channelNames = getChannelNamesByIds(ids);

    if (channelNames.length > 0) {
      return channelNames.join(", ");
    }

    // Fallback when channels aren't loaded yet
    const channelCount = ids.length;
    return `${channelCount} channel${channelCount !== 1 ? "s" : ""}`;
  };

  return {
    slackChannels,
    isLoading,
    error,
    getChannelNamesByIds,
    getChannelNameById,
    formatChannelDisplay,
  };
};
